import React from 'react';
import { useFormContext } from 'react-hook-form';
import { TextInput } from '@/components/form/TextInput.tsx';
import { NumberInput } from '@/components/form/NumberInput.tsx';
import CheckboxInput from '@/components/form/CheckboxInput.tsx';
import { type TourFormData } from '../schemas/tourSchema.ts';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/Card.tsx';
import ActionButton from '@/components/buttons/ActionButton.tsx';
import TwoInputsRow from '@/components/form/ui/TwoInputsRow.tsx';
import { DateInput } from '@/components/form/DateInput.tsx';
import DateRangeInput from '@/components/form/DateRangeInput.tsx';
import TimeInput from '@/components/form/TimeInput.tsx';
import CountrySelectInput from '@/components/form/CountrySelectInput.tsx';

const TourForm = ({
  onSubmit,
  isLoading,
  isEditMode = false,
  onNext,
  showSaveButton = true,
  showNextButton = false,
}: {
  onSubmit: (t: TourFormData) => void;
  isLoading: boolean;
  isEditMode?: boolean;
  onNext?: () => void;
  showSaveButton?: boolean;
  showNextButton?: boolean;
}) => {
  const {
    handleSubmit,
    register,
    control,
    watch,
    setValue,
    formState: { errors },
    getValues,
  } = useFormContext<TourFormData>();

  const hasParticipantLimit = watch('hasParticipantLimit');

  const onRegistrationStartDateChange = () => {
    const time = getValues('registrationStartDateTime');
    if (!time) {
      setValue('registrationStartDateTime', '00:00');
    }
  };

  const onRegistrationEndDateChange = () => {
    const time = getValues('registrationEndDateTime');
    if (!time) {
      setValue('registrationEndDateTime', '23:59');
    }
  };

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className="s mx-auto max-w-[600px] space-y-2"
    >
      <Card>
        <CardHeader>
          <CardTitle>Podstawowe informacje</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4 p-4">
          <TextInput
            label="Nazwa wyjazdu"
            register={register('tourName')}
            placeholder="Wpisz nazwę wyjazdu"
            required={true}
            error={errors.tourName?.message}
          />

          <TwoInputsRow>
            <CountrySelectInput<TourFormData>
              label="Kraj"
              fieldName="destination.country"
              control={control}
              required={true}
              error={errors.destination?.country?.message}
            />
            <TextInput
              label="Miejscowość"
              register={register('destination.city')}
              placeholder="Wpisz miejscowość"
              required={true}
              error={errors.destination?.city?.message}
            />
          </TwoInputsRow>

          <TwoInputsRow>
            <TextInput
              label="Ulica"
              register={register('destination.street')}
              placeholder="Wpisz ulicę"
              required={false}
              error={errors.destination?.street?.message}
            />
            <TextInput
              label="Kod pocztowy"
              register={register('destination.postalCode')}
              placeholder="np. 34-500"
              required={false}
              error={errors.destination?.postalCode?.message}
            />
          </TwoInputsRow>

          <DateRangeInput.Root
            startFieldName="startDate"
            endFieldName="endDate"
            control={control}
            required
          >
            <TwoInputsRow>
              <div className="flex flex-row items-end gap-1">
                <DateRangeInput.Start label="Data rozpoczęcia" required />
                <TimeInput
                  {...register('startDateTime')}
                  error={errors.startDateTime?.message}
                />
              </div>

              <div className="flex flex-row items-end gap-1">
                <DateRangeInput.End label="Data zakończenia" required />
                <TimeInput {...register('endDateTime')} />
              </div>
            </TwoInputsRow>
          </DateRangeInput.Root>

          <TwoInputsRow>
            <div className="flex flex-row items-end gap-1">
              <DateInput
                label="Otwarcie zapisów"
                dateFieldName="registrationStartDate"
                control={control}
                onChange={onRegistrationStartDateChange}
              />
              <TimeInput
                {...register('registrationStartDateTime')}
                error={errors.registrationStartDateTime?.message}
              />
            </div>

            <div className="flex flex-row items-end gap-1">
              <DateInput
                label="Zamknięcie zapisów"
                dateFieldName="registrationEndDate"
                control={control}
                onChange={onRegistrationEndDateChange}
              />
              <TimeInput
                {...register('registrationEndDateTime')}
                error={errors.registrationEndDateTime?.message}
              />
            </div>
          </TwoInputsRow>

          <CheckboxInput
            label="Zapisy w trakcie trwania wyjazdu"
            register={register('allowRegistrationDuringTour')}
            hint="Domyślnie zapisy gasną o północy dnia rozpoczęcia. Po zaznaczeniu trwają do końca dnia zakończenia wyjazdu — dotyczy też okna listy rezerwowej."
            error={errors.allowRegistrationDuringTour?.message}
          />

          {/*<SingleSelectInput*/}
          {/*  fieldName={'tourOrganizerInsurance'}*/}
          {/*  control={control}*/}
          {/*  options={[{ value: 'NONE', label: 'Brak ubezpieczenia' }]}*/}
          {/*  error={errors.tourOrganizerInsurance?.message}*/}
          {/*/>*/}

          <TextInput
            label="Link do czatu wyjazdu"
            register={register('tourChatInviteLink')}
            placeholder="np. https://whatsapp.com/grupa1234"
            required={false}
            error={errors.tourChatInviteLink?.message}
            hint="Opcjonalny link do grupy czatu wyjazdu"
          />
        </CardContent>
      </Card>

      <Card className="overflow-hidden">
        <CardHeader className="border-b px-4 py-4">
          <CardTitle>Dodatakowe ogrniczenia</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <CheckboxInput
              label="Ustaw limit uczestników"
              register={register('hasParticipantLimit')}
              error={errors.hasParticipantLimit?.message}
              onChange={(e) => {
                if (!e.target.checked) {
                  setValue('participantLimit', undefined, {
                    shouldValidate: true,
                    shouldDirty: true,
                  });
                }
              }}
            />

            <NumberInput
              label="Maksymalna liczba uczestników"
              register={register('participantLimit', {
                setValueAs: (value) => {
                  if (value === '' || value === null || value === undefined) {
                    return undefined;
                  }
                  const num = Number(value);
                  return isNaN(num) ? undefined : num;
                },
              })}
              placeholder="Wprowadź limit uczestników"
              min={1}
              disabled={!hasParticipantLimit}
              error={errors.participantLimit?.message}
              hint={
                !hasParticipantLimit
                  ? 'Zaznacz checkbox aby ustawić limit'
                  : undefined
              }
            />
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-between">
        <div className="flex gap-2">
          <ActionButton tone="ghost" size="sm" type="button">
            Anuluj
          </ActionButton>
        </div>
        <div className="flex gap-2">
          {showSaveButton && (
            <ActionButton
              tone="accent"
              size="sm"
              type="submit"
              isLoading={isLoading}
            >
              {isEditMode ? 'Zapisz zmiany' : 'Zapisz'}
            </ActionButton>
          )}
          {showNextButton && onNext && (
            <ActionButton
              tone="accent"
              size="sm"
              type="button"
              onClick={onNext}
            >
              Dalej
            </ActionButton>
          )}
        </div>
      </div>
    </form>
  );
};

export default TourForm;
